// src/pages/Manager/MorningMeeting.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  "To call out": the marks given since yesterday morning, beside
//               the agenda, so the meeting can open by recognising yesterday
//               without anybody remembering what they gave at four o'clock.
//               Labelled as not one of his five, because it is not.
//   2026-09-23  New. The second of Jazz's management artefacts.
//
// THE MORNING MEETING.
//
// His first daily discipline: "Morning meeting with results, appointments,
// deliveries, aged opportunities and one skill focus."
//
// PREPARED BEFORE, HELD AFTER, AND THE PRODUCT KNOWS THE DIFFERENCE.
//
// Read the trade before building this. Nobody complains that dealership
// morning meetings do not happen. They complain that nobody prepared: the
// meeting is held because it is Tuesday, whoever runs it scribbles two things
// on a pad as everybody sits down, and a floor gets pulled off revenue work for
// a talk fest. So the agenda is the artefact and it is written first, and
// `held_at` is stamped when it actually happened.
//
// THE WHOLE FLOOR CAN READ IT, which no other artefact in this product allows.
// A salesperson cannot see the audit, the numbers or somebody's sign off. They
// can see today's agenda, because the entire point of naming ONE skill focus is
// that everybody knows what today is about.
//
// The step link is the J13 thesis in a daily habit. Ninety seconds a day on one
// of the thirteen is how a course stops being something somebody finished once.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, HStack, VStack, Text, Textarea, Input, Button, Select, Spinner, Alert, AlertIcon,
  Checkbox, Badge,
} from '@chakra-ui/react'
import { AlertTriangle, Check, Sun } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { displayNameFor } from '../../lib/names'
import { STEPS } from '../../content/steps'
import { listFloor } from '../../lib/oneOnOne'
import {
  AGENDA, FOCUS, getMeeting, listRecent, saveMeeting, markHeld, lastDays,
  meetingState, agendaProgress, tooManyFocuses, heldStreak,
} from '../../lib/morningMeeting'
import { dayKey, addDays, parseDay } from '../../lib/localDate'
import { Link as RouterLink } from 'react-router-dom'
import Medallion from '../../components/ui/Medallion.jsx'
import { markByKey } from '../../content/marks'
import { firstNameFor } from '../../lib/names'
import { listSince, yesterdaysMarks, todaysMarks } from '../../lib/recognitions'

const RUN = 10

const STATE_COPY = {
  held: { label: 'held', color: 'success' },
  prepared: { label: 'prepared, not held', color: 'warn' },
  unprepared: { label: 'held without an agenda', color: 'danger' },
  empty: { label: 'nothing yet', color: 'inkDim' },
  none: { label: 'nothing yet', color: 'inkDim' },
}

export default function MorningMeeting() {
  const { user, profile } = useAuth()
  const dealershipId = profile?.dealership_id || null

  const [on, setOn] = useState(() => dayKey())
  const [draft, setDraft] = useState({})
  const [floor, setFloor] = useState([])
  const [recent, setRecent] = useState([])
  const [here, setHere] = useState([])
  const [marks, setMarks] = useState(null)
  const [state, setState] = useState({ loading: true, saving: false, error: null, saved: false })

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    const [m, f, r, k] = await Promise.all([
      getMeeting(dealershipId, on),
      listFloor(dealershipId),
      listRecent(dealershipId, RUN),
      listSince(dealershipId, parseDay(addDays(on, -1)).toISOString()),
    ])
    // Marks for the day being looked at: its own and the day before. A failed
    // read leaves null and the section does not render, rather than telling a
    // manager there is nobody to call out.
    // Only the floor's: a mark an owner gave a manager is real, and it is not
    // what a salesperson meeting calls out.
    const onFloor = new Set((f.data || []).map((p) => p.id))
    setMarks(
      k.data
        ? [...todaysMarks(k.data, on), ...yesterdaysMarks(k.data, on)].filter((r) => onFloor.has(r.given_to))
        : null
    )
    setDraft(m.data || {})
    setHere(m.data?.attended || [])
    setFloor(f.data)
    setRecent(r.data)
    const err = m.error || f.error || r.error
    setState({ loading: false, saving: false, error: err ? err.message || String(err) : null, saved: false })
  }, [dealershipId, on])

  useEffect(() => {
    load()
  }, [load])

  const days = useMemo(() => lastDays(RUN), [])
  const streak = useMemo(() => heldStreak(recent, days, dayKey()), [recent, days])
  const st = meetingState(draft)
  const progress = agendaProgress(draft)
  const focusWarn = tooManyFocuses(draft[FOCUS.key])

  const set = (key) => (v) => {
    setDraft((d) => ({ ...d, [key]: v }))
    setState((s) => ({ ...s, saved: false }))
  }

  const save = async () => {
    setState((s) => ({ ...s, saving: true, error: null, saved: false }))
    const fields = {}
    for (const f of AGENDA) fields[f.key] = draft[f.key]
    fields[FOCUS.key] = draft[FOCUS.key]
    fields.step_number = draft.step_number
    const { error } = await saveMeeting({ dealershipId, ledBy: user?.id, on, fields })
    if (error) {
      setState((s) => ({ ...s, saving: false, error }))
      return
    }
    await load()
    setState((s) => ({ ...s, saved: true }))
  }

  const hold = async () => {
    if (!draft.id) return
    const { error } = await markHeld({ id: draft.id, attended: here })
    if (error) {
      setState((s) => ({ ...s, error }))
      return
    }
    load()
  }

  if (state.loading) {
    return (
      <VStack align="stretch" maxW="sheet" spacing={6}>
        <Spinner size="sm" />
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          The first of his daily disciplines
        </Text>
        <Text textStyle="pageTitle">The morning meeting</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          Results, appointments, deliveries, aged opportunities and one skill focus. Written before
          you walk in, because the thing everybody hates about these is not that they happen, it is
          that nobody prepared.
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!dealershipId && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          Your account is not attached to a store, so there is no floor to hold one with.
        </Alert>
      )}

      {/* THE RUN OF DAYS. His audit asks "morning meeting completed" as a yes or
          no, and a yes or no cannot show a habit slipping. */}
      <Box>
        <HStack align="baseline" justify="space-between" mb={2} flexWrap="wrap" gap={2}>
          <Text fontSize="lg" fontWeight={600}>
            The last {RUN} days
          </Text>
          <Text fontSize="sm" fontFamily="mono" color={streak.held === RUN ? 'success' : 'inkDim'}>
            {streak.held} of {RUN} held
          </Text>
        </HStack>
        <HStack spacing={1.5} flexWrap="wrap" rowGap={1.5}>
          {[...days].reverse().map((d) => {
            const row = recent.find((r) => r.meeting_on === d)
            const s = meetingState(row)
            const color = s === 'held' ? 'success' : s === 'unprepared' ? 'danger' : s === 'prepared' ? 'warn' : 'rail2'
            return (
              <Box
                key={d}
                title={`${d}, ${STATE_COPY[s].label}`}
                w="26px"
                h="26px"
                borderRadius="3px"
                bg={s === 'empty' || s === 'none' ? 'rail' : color}
                border="1px solid"
                borderColor={s === 'empty' || s === 'none' ? 'line' : color}
              />
            )
          })}
        </HStack>
        <Text fontSize="sm" color="inkDim" mt={2} maxW="measure">
          Filled is held. Amber is an agenda nobody ran. Red is a meeting held with nothing written
          down first.
        </Text>
      </Box>

      {/* TO CALL OUT. Not one of his five and it says so. It is item six of
          his weekly rhythm, recognition of process behaviours, and the
          morning meeting is where a floor hears it. */}
      {marks && (
        <Box bg="goldSoft" borderRadius="card" px={5} py={4}>
          <HStack justify="space-between" align="baseline" mb={marks.length ? 3 : 1} flexWrap="wrap" gap={2}>
            <Text fontWeight={600} fontSize="body">
              To call out
            </Text>
            <Text fontSize="xs" color="inkMuted">
              Not one of his five. Thirty seconds, before results.
            </Text>
          </HStack>
          {marks.length === 0 ? (
            <Text fontSize="sm" color="inkMuted" lineHeight={1.5}>
              No marks since yesterday morning. If you saw somebody run a step well,{' '}
              <Box as={RouterLink} to="/manager/" textDecoration="underline" textUnderlineOffset="3px">
                give one from the floor
              </Box>{' '}
              and say it out loud in here.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2.5}>
              {marks.map((r) => {
                const person = floor.find((p) => p.id === r.given_to)
                const mk = markByKey(r.mark)
                return (
                  <HStack key={r.id} spacing={3} align="flex-start">
                    <Medallion mark={r.mark} size={30} />
                    <Box minW={0}>
                      <Text fontSize="body-sm" lineHeight={1.4}>
                        <Text as="span" fontWeight={600}>{firstNameFor(person) || 'Somebody'}</Text>
                        <Text as="span" color="inkMuted">{' · '}{mk?.name || 'a mark'}</Text>
                      </Text>
                      {r.note && (
                        <Text fontSize="sm" color="inkMuted" lineHeight={1.45}>
                          “{r.note}”
                        </Text>
                      )}
                    </Box>
                  </HStack>
                )
              })}
            </VStack>
          )}
        </Box>
      )}

      <Box>
        <HStack justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
          <HStack spacing={3}>
            <Sun size={16} aria-hidden="true" />
            <Input
              id="mm-date"
              type="date"
              value={on}
              onChange={(e) => setOn(e.target.value)}
              maxW="11rem"
              fontFamily="mono"
            />
            <Badge colorScheme={st === 'held' ? 'green' : st === 'unprepared' ? 'red' : 'gray'} fontFamily="mono" fontSize="xs">
              {STATE_COPY[st].label}
            </Badge>
          </HStack>
          <Text fontSize="sm" color="inkDim" fontFamily="mono">
            {state.saving ? 'saving' : state.saved ? 'saved' : `${progress.done} of ${progress.total}`}
          </Text>
        </HStack>

        <VStack align="stretch" spacing={0}>
          {AGENDA.map((f) => (
            <Box key={f.key} py={3} borderTop="1px solid" borderColor="line">
              <Text fontWeight={600} fontSize="body" mb={0.5}>
                {f.label}
              </Text>
              <Text fontSize="sm" color="inkMuted" mb={2} maxW="measure" lineHeight={1.5}>
                {f.prompt}
              </Text>
              <Textarea
                value={draft[f.key] || ''}
                onChange={(e) => set(f.key)(e.target.value)}
                rows={2}
                fontSize="body"
              />
            </Box>
          ))}

          <Box py={3} borderTop="1px solid" borderColor="line">
            <Text fontWeight={600} fontSize="body" mb={0.5}>
              {FOCUS.label}
            </Text>
            <Text fontSize="sm" color="inkMuted" mb={2} maxW="measure" lineHeight={1.5}>
              {FOCUS.prompt}
            </Text>
            <Textarea
              value={draft[FOCUS.key] || ''}
              onChange={(e) => set(FOCUS.key)(e.target.value)}
              rows={2}
              fontSize="body"
            />
            {focusWarn && (
              <HStack mt={2} spacing={2} color="warn" align="flex-start">
                <Box pt="2px">
                  <AlertTriangle size={14} aria-hidden="true" />
                </Box>
                <Text fontSize="sm" maxW="measure" lineHeight={1.5}>
                  That reads like more than one. His word is one, and choosing is the discipline. You
                  can still save it.
                </Text>
              </HStack>
            )}
            <HStack mt={3} spacing={2} flexWrap="wrap" rowGap={2}>
              <Text fontSize="sm" color="inkMuted">
                Which step
              </Text>
              <Select
                id="mm-step"
                value={draft.step_number ?? ''}
                onChange={(e) => set('step_number')(e.target.value)}
                maxW="19rem"
                size="sm"
                placeholder="Not one of the thirteen"
              >
                {STEPS.map((s) => (
                  <option key={s.number} value={s.number}>
                    {s.number}. {s.title}
                  </option>
                ))}
              </Select>
            </HStack>
          </Box>
        </VStack>

        <HStack mt={5} spacing={3} align="center" flexWrap="wrap" rowGap={2}>
          <Button onClick={save} isLoading={state.saving} isDisabled={!dealershipId}>
            Save the agenda
          </Button>
          {draft.id && !draft.held_at && (
            <Button variant="outline" leftIcon={<Check size={14} />} onClick={hold}>
              We held it
            </Button>
          )}
          {draft.held_at && (
            <Text fontSize="sm" color="success" fontFamily="mono">
              held
            </Text>
          )}
        </HStack>

        {draft.id && !draft.held_at && floor.length > 0 && (
          <Box mt={5}>
            <Text fontSize="sm" color="inkMuted" mb={2} maxW="measure">
              Who was in it. Optional, and it is the difference between the meeting happening and the
              floor being in it.
            </Text>
            <VStack align="stretch" spacing={1}>
              {floor.map((p) => (
                <Checkbox
                  key={p.id}
                  isChecked={here.includes(p.id)}
                  onChange={(e) =>
                    setHere((h) => (e.target.checked ? [...h, p.id] : h.filter((x) => x !== p.id)))
                  }
                >
                  <Text fontSize="body">{displayNameFor(p) || 'Somebody'}</Text>
                </Checkbox>
              ))}
            </VStack>
          </Box>
        )}
      </Box>
    </VStack>
  )
}
